import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Error } from "@progress/kendo-react-labels";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import jwt_decode from "jwt-decode";
import Feature from "ol/Feature.js";
import { LineString, Point, Polygon, MultiPoint } from "ol/geom.js";
import { Draw, Modify } from "ol/interaction.js";
import VectorLayer from "ol/layer/Vector.js";
import { Vector as VectorSource } from "ol/source";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCookie } from "../../api/axios";
import { clearEdits, setActiveCreateFeatureTool } from "../../redux/editingSlice";
import { setSnappingReady } from "../../redux/setConfigSlice";
import { makeArray } from "../../redux/stateChangers";
import { setActiveSideBarTab, setActiveTool } from "../../redux/toolSelectSlice";
import MapContext from "../MapContext";
import { Snapping } from "../Markup/Snapping";
import { getFeatureWithId, getMapLayerById } from "../utils/mapUtils";
import GPSTrimble from "./GPSTrimble";
import Input from "./Input";
import { UpdateFeatureData, encodeDefaultValue } from "./editUtils";

const EditFeature = (prop) => {
    const dispatch = useDispatch();

    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const editMode = useSelector((state) => state.editingOptions.activeCreateFeatureTool);
    const editEditFeatureId = useSelector(
        (state) => state.editingOptions.currentEditFeature
    );

    const activeCreateFeatureTool = useSelector(
        (state) => state.editingOptions.activeCreateFeatureTool
    );

    const [points, setPoints] = useState([]);
    const [undoPoints, setUndoPoints] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [snapping, setSnapping] = useState(false);

    const [editVertexIndex, setEditVertexIndex] = useState(null);
    const editLayerID = useSelector((state) => state.editingOptions.currentEditLayer);
    const { map } = useContext(MapContext);
    const [editErrorMessage, setEditErrorMessage] = useState("");
    const [editPage, setEditPage] = useState("");
    let parentInfo = prop.prop;
    const token = getCookie("ast");

    let userGivenName = "Guest";
    let tokenRoles = ["GUEST"];
    if (token) {
        userGivenName = jwt_decode(token).preferred_username;
        tokenRoles = jwt_decode(token).realm_access.roles;
    }

    let permArr = makeArray(parentInfo.selectedLayer.Permissions.Permission);

    let geometryUpdateRoles = permArr.filter((a) =>
        permArr
            .filter((a) => a.GeometryEditable)
            .map((b) => b.Value)
            .includes(a.Value)
    );
    const locValue = useRef();
    const [formValues, setFormValues] = useState([]);
    let currentFeature = getFeatureWithId(map, editLayerID, editEditFeatureId);

    useEffect(() => {
        return () => {
            setEditPage("");
        };
    }, []);

    useEffect(() => {
        if (map) {
            addSketchLayer();
            addSnapSketchLayer();
        }
        return () => {
            map.removeLayer(getMapLayerById(map, "newFeatureSketchLayer"));
            map.removeLayer(getMapLayerById(map, "newFeatureSnapSketchLayer"));
        };
    }, [map]);

    useEffect(() => {
        let l = mapConfig.EditLayers.filter((i) => {
            return i.Id === editLayerID;
        })[0];
        if (l) {
            parentInfo.selectedLayer = l;
        }
        let currentFeature = getFeatureWithId(map, editLayerID, editEditFeatureId);
        let shape = [];
        if (currentFeature) {
            let i = 0;
            if (currentFeature.values_.geometry.layout === "XY") {
                for (
                    let index = 0;
                    index < currentFeature.values_.geometry.flatCoordinates.length;
                    index += 2
                ) {
                    const xVal = currentFeature.values_.geometry.flatCoordinates[index];
                    const yVal =
                        currentFeature.values_.geometry.flatCoordinates[index + 1];
                    shape.push({ Index: i, X: xVal, Y: yVal, Z: 0 });
                    i++;
                }
            } else if (currentFeature.values_.geometry.layout === "XYZ") {
                for (
                    let index = 0;
                    index < currentFeature.values_.geometry.flatCoordinates.length;
                    index += 3
                ) {
                    const xVal = currentFeature.values_.geometry.flatCoordinates[index];
                    const yVal =
                        currentFeature.values_.geometry.flatCoordinates[index + 1];
                    const zVal =
                        currentFeature.values_.geometry.flatCoordinates[index + 2];
                    shape.push({ Index: i, X: xVal, Y: yVal, Z: zVal });
                    i++;
                }
            }
            if (parentInfo.selectedLayer.GeometryType.toLowerCase() === "polygon") {
                shape.pop();
            }
            parentInfo.shape = shape;
            setPoints((current) => {
                return shape;
            });
            setFormValues(() => {
                return [];
            });
        }
    }, [editEditFeatureId, editLayerID]);

    useEffect(() => {
        let fieldsList = makeArray(parentInfo.selectedLayer.Fields.Field);
        if (formValues.length === 0 && fieldsList.length > 0) {
            let val = [];
            fieldsList.map((el) => {
                if (el.Visible || el.Type === "GUID") {
                    let currentFieldVal = currentFeature.values_[el.Name];
                    val.push({
                        id: `input_${el.Name}`,
                        label: el.DisplayName ?? el.Name,
                        type: el.Type?.toLowerCase() ?? "string",
                        value:
                            currentFieldVal ??
                            encodeDefaultValue(
                                el.DefaultValue,
                                userGivenName,
                                parentInfo.addressIn,
                                parentInfo.shape
                            ) ??
                            "",
                        domains: el.Domains,
                        charLimit: el.CharLimit,
                    });
                }
            });
            setFormValues(() => {
                return val;
            });
        }
    }, [formValues]);

    useEffect(() => {
        const markupSource = getMapLayerById(map, "newFeatureSketchLayer").getSource();
        const markupSnappingSource = getMapLayerById(
            map,
            "newFeatureSnapSketchLayer"
        ).getSource();

        if (activeCreateFeatureTool === "map") {
            //Add Map Click Interaction
            addInteraction("Point", markupSource, markupSnappingSource);
            if (snapping) {
                markupSnappingSource.on("addfeature", mapPointSingleClickSnapping);
                dispatch(setSnappingReady(true));
                map.un("singleclick", mapPointSingleClick);
                map.un("dblclick", mapPointDoubleClick);
            } else {
                map.on("singleclick", mapPointSingleClick);
                map.on("dblclick", mapPointDoubleClick);
                markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            }
        } else {
            //remove Map Click Interaction
            map.removeInteraction(draw.current);
            map.removeInteraction(modifyInteract.current);
            markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            map.un("singleclick", mapPointSingleClick);
            map.un("dblclick", mapPointDoubleClick);
        }
        setEditVertexIndex(null);
        return () => {
            map.removeInteraction(draw.current);
            map.removeInteraction(modifyInteract.current);
            markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            map.un("singleclick", mapPointSingleClick);
            map.un("dblclick", mapPointDoubleClick);
        };
    }, [activeCreateFeatureTool, snapping]);

    useEffect(() => {
        if (points) {
            updateSketchLayer(points);
        }
    }, [points]);

    const draw = useRef();
    const modifyInteract = useRef();
    const addInteraction = (tool, markupSource, markupSnappingSource) => {
        const drawType = tool;
        draw.current = new Draw({
            source: markupSnappingSource,
            type: drawType,
        });
        modifyInteract.current = new Modify({
            source: markupSource,
        });

        modifyInteract.current.on("modifyend", (e) => {
            let i = 0;
            if (e.features.array_[0].getGeometry().getType() === "Point") {
                let c = e.features.array_[0].getGeometry().getCoordinates();
                let a = [{ Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 }];
                setPoints(a);
            } else if (e.features.array_[0].getGeometry().getType() === "LineString") {
                let a = e.features.array_[0]
                    .getGeometry()
                    .getCoordinates()
                    .map((c) => {
                        return { Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 };
                    });
                setPoints(a);
            } else if (e.features.array_[0].getGeometry().getType() === "Polygon") {
                let a = e.features.array_[0]
                    .getGeometry()
                    .getCoordinates()
                    .flat()
                    .map((c) => {
                        return { Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 };
                    });
                a = a.slice(0, a.length - 1);
                setPoints(a);
            }
        });
        map.addInteraction(draw.current);
        map.addInteraction(modifyInteract.current);
    };

    const saveCallback = (evt) => {
        if (evt.error) {
            setEditErrorMessage(evt.error);
        } else {
            let layer = getMapLayerById(map, evt.layer.Id);
            layer.getSource().refresh();
            let layerLabel = getMapLayerById(map, evt.layer.Id + "_label")?.getSource();
            if (layerLabel) {
                layerLabel.refresh();
            }
            setEditErrorMessage("");
            dispatch(clearEdits());
            dispatch(setActiveTool(""));
            dispatch(setActiveSideBarTab(2));
        }
    };

    const handleChange = (e, index) => {
        const values = [...formValues];
        values[index].value = e.target.value;
        setFormValues(values);
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        let attArr = formValues.map((val) => {
            let value;
            if (val.type === "date" && typeof val.value === "number") {
                value = val.value;
            } else if (val.type === "date" && typeof val.value === "object") {
                value = val.value.valueOf();
            } else if (val.type === "date" && typeof val.value === "string") {
                let temp = new Date(val.value);
                if (temp.toString() !== "Invalid Date") {
                    value = temp.valueOf();
                }
            } else {
                value = val.value;
            }
            return {
                fieldName: val.id.replace("input_", ""),
                fieldValue: value,
            };
        });
        UpdateFeatureData(
            editEditFeatureId,
            parentInfo.selectedLayer,
            attArr,
            points,
            saveCallback
        );
    };

    const handleCancel = (e) => {
        dispatch(clearEdits());
        dispatch(setActiveTool(""));
        dispatch(setActiveSideBarTab(2));
        setEditPage("");
    };

    const handleEditGeometry = (e) => {
        setUndoPoints(points);
        setEditPage("Geometry");
        dispatch(setActiveCreateFeatureTool("map"));
    };

    const handleSaveGeometry = (e) => {
        setUndoPoints(points);
        setEditPage("");
        dispatch(setActiveCreateFeatureTool(""));
    };

    const handleCancelGeometry = (e) => {
        setEditPage("");
        setPoints(undoPoints);
        dispatch(setActiveCreateFeatureTool(""));
    };

    // Geometry Edit Button Handlers
    const handleMapButton = () => {
        dispatch(setActiveCreateFeatureTool("map"));
    };
    const handleManualButton = () => {
        if (activeCreateFeatureTool === "manual") {
            dispatch(setActiveCreateFeatureTool("map"));
        } else {
            dispatch(setActiveCreateFeatureTool("manual"));
        }
    };
    const handleGPSButton = () => {
        if (activeCreateFeatureTool === "gps") {
            dispatch(setActiveCreateFeatureTool("map"));
        } else {
            dispatch(setActiveCreateFeatureTool("gps"));
        }
    };
    const gpsPoint = () => {
        AddOrUpdateVertex(
            Math.floor(Math.random() * (1510000 - 1450000) + 1450000),
            Math.floor(Math.random() * (690000 - 650000) + 650000),
            Math.floor(Math.random() * 50)
        );
    };
    const gpsSetting = () => {
        console.log("GPS Setting");
    };

    // Map Interaction Handlers
    const mapPointSingleClick = (e) => {
        let coor = map.getCoordinateFromPixel(map.getEventPixel(e.originalEvent));
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };
    const mapPointSingleClickSnapping = (e) => {
        const markupSource = getMapLayerById(
            map,
            "newFeatureSnapSketchLayer"
        ).getSource();
        markupSource.clear();
        let coor = e.feature.getGeometry().getCoordinates();
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };
    const mapPointDoubleClick = (e) => {
        let coor = map.getCoordinateFromPixel(map.getEventPixel(e.originalEvent));
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };

    //Geometry Handlers
    const updateSketchLayer = (pointsArr) => {
        if (pointsArr.length === 0) {
            return;
        }
        const sketchLayer = getMapLayerById(map, "newFeatureSketchLayer");
        if (!sketchLayer) {
            return;
        }

        let style = [
            new Style({
                fill: new Fill({
                    color: "rgb(255, 0, 211,0.5)",
                }),
                stroke: new Stroke({
                    color: "rgb(255, 158, 211)",
                    width: 4,
                }),
            }),
            new Style({
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({
                        color: "rgb(255, 0, 211)",
                    }),
                }),
                geometry: function (feature) {
                    // return the coordinates of all rings of the polygon
                    let coordinates = feature.getGeometry().getCoordinates();
                    if (feature.getGeometry().getType() === "Point") {
                        return new Point(coordinates);
                    }
                    if (feature.getGeometry().getType() === "Polygon") {
                        coordinates = coordinates.flat();
                    }
                    return new MultiPoint(coordinates);
                },
            }),
        ];

        sketchLayer.getSource().clear();
        if (parentInfo.selectedLayer.GeometryType === "Point") {
            pointsArr.forEach((point) => {
                let xVal = parseFloat(point.X);
                let yVal = parseFloat(point.Y);
                let zVal = parseFloat(point.Z);

                let sketch = new Feature(new Point([xVal, yVal, zVal]));
                sketch.setStyle(style);

                sketchLayer.getSource().addFeature(sketch);
            });
        }
        if (
            parentInfo.selectedLayer.GeometryType === "Line" ||
            parentInfo.selectedLayer.GeometryType === "Polyline"
        ) {
            let vertices = points.map((i) => [i.X, i.Y, i.Z]);

            let sketchFeature = new Feature(new LineString([...vertices]));
            sketchFeature.setId("sketchLine");
            sketchFeature.setStyle(style);
            sketchLayer.getSource().addFeatures([sketchFeature]);
        }
        if (parentInfo.selectedLayer.GeometryType === "Polygon") {
            //map the vertices from states to a usable format
            let vertices = points.map((i) => [i.X, i.Y, i.Z]);
            //include the first Vertex at the end of the list to close polygon.
            let sketchFeature = new Feature(new Polygon([[...vertices, vertices[0]]]));
            sketchFeature.setId("sketchPolygon");
            sketchFeature.setStyle(style);
            sketchLayer.getSource().addFeatures([sketchFeature]);
        }
    };

    const AddOrUpdateVertex = (X, Y, Z) => {
        setHasError(false);
        setErrorMsg("");

        if (parentInfo.selectedLayer.GeometryType === "Point") {
            setPoints((current) => {
                return [{ Index: 0, X: X, Y: Y, Z: Z }];
            });
        } else {
            setPoints((current) => {
                return [...current, { Index: current.length, X: X, Y: Y, Z: Z }];
            });
        }
    };

    // Map Layer
    const addSketchLayer = () => {
        let sketchLayer = new VectorLayer({
            source: new VectorSource(),
            properties: { Id: "newFeatureSketchLayer" },
        });
        sketchLayer.setZIndex(100000001);
        map.addLayer(sketchLayer);
    };
    const addSnapSketchLayer = () => {
        let sketchLayer = new VectorLayer({
            source: new VectorSource(),
            properties: { Id: "newFeatureSnapSketchLayer" },
        });
        sketchLayer.setZIndex(10000001);
        map.addLayer(sketchLayer);
    };

    //Geometry Grid Handlers

    const onItemChange = (event) => {
        const editedItemID = event.dataItem.Index;
        const data = points.map((item) =>
            item.Index === editedItemID ? { ...item, [event.field]: event.value } : item
        );
        setPoints((current) => {
            return data;
        });
    };

    const rowClick = (event) => {
        if (activeCreateFeatureTool === "manual") {
            setEditVertexIndex(event.dataItem.Index);
        }
    };

    const rowDoubleClick = (event) => {
        let pArr = points.filter(function (obj) {
            return obj.Index !== event.dataItem.Index;
        });
        let index = 0;
        pArr.forEach((element) => {
            element.Index = index++;
        });
        setPoints((current) => {
            return pArr;
        });
    };
    const addRecord = () => {
        const newRecord = {
            Index: points.length + 1,
        };
        setPoints((current) => {
            return [...points, newRecord];
        });
        setEditVertexIndex(newRecord.Index);
    };

    return (
        <>
            {!editPage && (
                <>
                    {" "}
                    <h3 className="title">Edit {parentInfo.selectedLayer.DisplayName}</h3>
                    <h4>Attributes</h4>
                    <>{locValue.current}</>
                    <form onSubmit={handleSubmit}>
                        {formValues.map((obj, index) => (
                            <Input
                                key={index}
                                objValue={obj}
                                onChange={handleChange}
                                index={index}
                            />
                        ))}
                        {editErrorMessage && <Error>{editErrorMessage}</Error>}
                        <Button
                            type={"button"}
                            onClick={handleCancel}
                            title="Cancel"
                            className="k-button k-button-md k-rounded-md k-button-solid submit-button">
                            Cancel
                        </Button>
                        <Button
                            type={"submit"}
                            title="Submit"
                            className="k-button k-button-md k-rounded-md k-button-solid submit-button">
                            Save
                        </Button>
                        {geometryUpdateRoles.length > 0 && (
                            <Button
                                type={"button"}
                                onClick={handleEditGeometry}
                                title="Edit Geometry"
                                className="k-button k-button-md k-rounded-md k-button-solid submit-button">
                                Edit Geometry
                            </Button>
                        )}
                    </form>
                </>
            )}
            {editPage === "Geometry" && (
                <>
                    {" "}
                    <h3 className="title">Edit {parentInfo.selectedLayer.DisplayName}</h3>
                    <h4>Shape</h4>
                    <>{locValue.current}</>
                    <div className="edit-option">
                        <p>Select Entry Method</p>
                        <ButtonGroup>
                            <Button
                                togglable={true}
                                selected={
                                    activeCreateFeatureTool === "map" ? true : false
                                }
                                onClick={handleMapButton}>
                                Map
                            </Button>
                            <Button
                                togglable={true}
                                selected={
                                    activeCreateFeatureTool === "manual" ? true : false
                                }
                                onClick={handleManualButton}>
                                Manual
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className="edit-option">
                        {hasError && <p className="edit-error">{errorMsg}</p>}
                        <Grid
                            editField="inEdit"
                            style={points.length > 6 ? { height: "30vh" } : {}}
                            data={points.map((item) => ({
                                ...item,
                                inEdit: item.Index === editVertexIndex,
                            }))}
                            onItemChange={onItemChange}
                            onRowClick={rowClick}
                            onRowDoubleClick={rowDoubleClick}>
                            {activeCreateFeatureTool === "manual" &&
                                (points.length === 0 ||
                                    parentInfo.selectedLayer.GeometryType !==
                                        "Point") && (
                                    <GridToolbar>
                                        <Button
                                            title="Add new"
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                            onClick={addRecord}>
                                            Add new
                                        </Button>
                                    </GridToolbar>
                                )}
                            {activeCreateFeatureTool === "gps" && (
                                <>
                                    <GridToolbar>
                                        {points.length === 1 &&
                                        parentInfo.selectedLayer.GeometryType ===
                                            "Point" ? (
                                            <Button
                                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-success"
                                                onClick={gpsPoint}>
                                                Update Point
                                            </Button>
                                        ) : (
                                            <Button
                                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                                onClick={gpsPoint}>
                                                Add Point
                                            </Button>
                                        )}
                                        <Button onClick={gpsSetting}>GPS Setting</Button>
                                    </GridToolbar>
                                </>
                            )}

                            <GridColumn
                                field="Index"
                                title="Index"
                                editor="numeric"
                                width="0"
                                editable={false}
                            />
                            <GridColumn field="X" title="X" editor="text" />
                            <GridColumn field="Y" title="Y" editor="text" />
                            <GridColumn field="Z" title="Z" editor="text" />
                        </Grid>
                    </div>
                    {activeCreateFeatureTool === "gps" && <GPSTrimble />}
                    {activeCreateFeatureTool === "map" && (
                        <div className="edit-option">
                            <Snapping setSnapping={setSnapping} snapping={snapping} />
                        </div>
                    )}
                    <Button
                        type={"button"}
                        onClick={handleSaveGeometry}
                        title="Save Geometry"
                        className="k-button k-button-md k-rounded-md k-button-solid submit-button">
                        Save Geometry
                    </Button>
                    <Button
                        type={"button"}
                        onClick={handleCancelGeometry}
                        title="Cancel Geometry"
                        className="k-button k-button-md k-rounded-md k-button-solid submit-button">
                        Cancel Geometry
                    </Button>
                </>
            )}
        </>
    );
};

export default EditFeature;
const drawStyle = new Style({
    zIndex: 1000,
    fill: new Fill({
        color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
        color: "rgba(0, 0, 0, 0.5)",
        width: 1.5,
    }),
    image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
        }),
        fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
        }),
    }),
});
